import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClockCircleOutlined, StarOutlined, StarFilled } from '@ant-design/icons';
import { useNavigationStore } from '../store/navigationStore';
import { usePageFavourite } from '../hooks/useActivityTracker';
import type { RecentPage, FavouritePage } from '../types';
import { NAV_CATEGORY_ICONS } from '../types';
import '../styles/ActivityNav.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(timestamp: number): string {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 172800) return 'Yesterday';
    return `${Math.floor(seconds / 86400)}d ago`;
}

type DropdownId = 'recents' | 'favourites' | null;

// ─── Component ───────────────────────────────────────────────────────────────

function ActivityNav() {
    const navigate = useNavigate();
    const { recents, favourites, clearRecents, clearFavourites, openPalette } =
        useNavigationStore();
    const { isFavourite, toggleFavourite, currentPage } = usePageFavourite();

    const [openDropdown, setOpenDropdown] = useState<DropdownId>(null);

    const close = useCallback(() => setOpenDropdown(null), []);

    const toggle = useCallback(
        (id: 'recents' | 'favourites') =>
            setOpenDropdown(prev => (prev === id ? null : id)),
        [],
    );

    // Escape key closes dropdown
    useEffect(() => {
        if (!openDropdown) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [openDropdown, close]);

    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
            close();
        },
        [navigate, close],
    );

    // ── Recents (capped at 10) ──

    const visibleRecents = recents.slice(0, 10);

    // ── Render ──

    return (
        <div className="activity-nav">
            {/* Recents button */}
            <button
                className={`activity-nav__btn ${openDropdown === 'recents' ? 'activity-nav__btn--active' : ''
                    }`}
                onClick={() => toggle('recents')}
                type="button"
            >
                <ClockCircleOutlined />
                {recents.length > 0 && (
                    <span className="activity-nav__badge">{recents.length}</span>
                )}
            </button>

            {/* Favourites button */}
            <button
                className={`activity-nav__btn ${openDropdown === 'favourites'
                        ? 'activity-nav__btn--active'
                        : ''
                    }`}
                onClick={() => toggle('favourites')}
                type="button"
            >
                <span className={currentPage && isFavourite ? '' : 'activity-nav__star--outline'}>
                    <StarFilled />
                </span>
                {favourites.length > 0 && (
                    <span className="activity-nav__badge">
                        {favourites.length}
                    </span>
                )}
            </button>

            {/* Click-outside overlay */}
            {openDropdown && (
                <div className="activity-nav__overlay" onClick={close} />
            )}

            {/* Recents dropdown */}
            <div
                className={`activity-nav__dropdown ${openDropdown === 'recents'
                        ? 'activity-nav__dropdown--visible'
                        : ''
                    }`}
            >
                <div className="activity-nav__header">
                    <span className="activity-nav__title">Recent Pages</span>
                    {recents.length > 0 && (
                        <button
                            className="activity-nav__clear"
                            onClick={clearRecents}
                            type="button"
                        >
                            Clear
                        </button>
                    )}
                </div>

                {visibleRecents.length === 0 ? (
                    <div className="activity-nav__empty">No recent pages</div>
                ) : (
                    <ul className="activity-nav__list">
                        {visibleRecents.map((page: RecentPage) => (
                            <li
                                key={page.path}
                                className="activity-nav__item"
                                onClick={() => handleNavigate(page.path)}
                            >
                                <span className="activity-nav__item-icon">
                                    {NAV_CATEGORY_ICONS[page.category]}
                                </span>
                                <div className="activity-nav__item-content">
                                    <div className="activity-nav__item-label">
                                        {page.label}
                                    </div>
                                    <div className="activity-nav__item-meta">
                                        <span className="activity-nav__time">
                                            {timeAgo(page.lastVisited)}
                                        </span>
                                        {page.visitCount > 1 && (
                                            <span className="activity-nav__visit-count">
                                                {page.visitCount}×
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="activity-nav__footer">
                    <div className="activity-nav__hint">
                        <kbd>Ctrl+K</kbd> to search all pages
                    </div>
                </div>
            </div>

            {/* Favourites dropdown */}
            <div
                className={`activity-nav__dropdown ${openDropdown === 'favourites'
                        ? 'activity-nav__dropdown--visible'
                        : ''
                    }`}
            >
                <div className="activity-nav__header">
                    <span className="activity-nav__title">Favourite Pages</span>
                    {favourites.length > 0 && (
                        <button
                            className="activity-nav__clear"
                            onClick={clearFavourites}
                            type="button"
                        >
                            Clear
                        </button>
                    )}
                </div>

                {/* Toggle current page favourite */}
                {currentPage && (
                    <div
                        className="activity-nav__item activity-nav__toggle-row"
                        onClick={toggleFavourite}
                    >
                        <span className="activity-nav__item-icon">
                            {isFavourite ? <StarFilled /> : <StarOutlined />}
                        </span>
                        <div className="activity-nav__item-content">
                            <div className="activity-nav__item-label">
                                {isFavourite ? 'Remove current page' : 'Add current page'}
                            </div>
                        </div>
                    </div>
                )}

                {favourites.length === 0 ? (
                    <div className="activity-nav__empty">
                        No favourite pages yet.
                    </div>
                ) : (
                    <ul className="activity-nav__list">
                        {favourites.map((page: FavouritePage) => (
                            <li
                                key={page.path}
                                className="activity-nav__item"
                                onClick={() => handleNavigate(page.path)}
                            >
                                <span className="activity-nav__item-icon">
                                    {NAV_CATEGORY_ICONS[page.category]}
                                </span>
                                <div className="activity-nav__item-content">
                                    <div className="activity-nav__item-label">
                                        {page.label}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export { ActivityNav };
